import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { LineService } from 'app/core/services/line.service';
import { UserService } from 'app/core/services/user.service';
import { User } from '@app/shared/models';
import { Line } from 'app/shared/models/line.model';
import { ToolFormDetails, ToolFormModel } from '../../tool-create.model';
import { Log } from 'ng2-logger';
import { ToolService } from '@app/core/services/tool.service';
import { of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { ToolProperty } from '@app/tool/model/enums/tool-property.enum';
import { ToolPropertyValidationState } from '@app/tool/model/tool-property-validation-state.enum';

export function ValidateOwner(control: AbstractControl) {
    if (control.value instanceof User) {
        return null;
    }
    return { invalidDropdown: true };
}

export function ValidateLine(control: AbstractControl) {
    if (control.value instanceof Line) {
        return null;
    }
    return { invalidDropdown: true };
}

@Component({
    selector: 'app-tool-details-form',
    templateUrl: 'tool-details-form.component.html',
    styleUrls: ['tool-details-form.component.scss']
})
export class ToolDetailsFormComponent implements OnInit, OnChanges {
    _logger = Log.create(ToolDetailsFormComponent.name);

    @Input() tool: ToolFormModel = new ToolFormModel();
    @Input() owners: User[];
    @Input() showValidations = false;

    @Output() changedSetSize = new EventEmitter<any>();
    @Output() setSwitchToggled = new EventEmitter<any>();
    @Output() changedLine = new EventEmitter<any>();

    form: UntypedFormGroup;
    selectedLine: Line;
    selectedOwner: User;

    availableLines: Line[];

    constructor(
        private lineService: LineService,
        private userService: UserService,
        private toolService: ToolService) {}

    ngOnInit() {
        this._logger.info('ngOnInit::', this.tool);
        this.form = new UntypedFormGroup({
            isSet: new UntypedFormControl(!!this.tool.isSet),
            setSize: new UntypedFormControl(this.tool.setSize, [Validators.min(1), Validators.max(300)]),
            nrOfSpareTools: new UntypedFormControl(this.tool.nrOfSpareTools),
            name: new UntypedFormControl(this.tool.name,Validators.required),
            description: new UntypedFormControl(this.tool.description, Validators.required),
            owner: new UntypedFormControl(this.tool.owner, [Validators.required, ValidateOwner]),
            line: new UntypedFormControl(this.tool.line, [Validators.required, ValidateLine]),
            isToolLifeMonitoredInSap: new UntypedFormControl(!!this.tool.isToolLifeMonitoredInSap),
            isToolLifeMonitoredInMes: new UntypedFormControl(!!this.tool.isToolLifeMonitoredInMes),
            isToolLifeMonitoredInPlc: new UntypedFormControl(!!this.tool.isToolLifeMonitoredInPlc),
            hasMaintenaceAtLine: new UntypedFormControl(!!this.tool.hasMaintenaceAtLine),
            isToolStationedAtERSA: new UntypedFormControl(!!this.tool.isToolStationedAtERSA),
            excludedFromReturn: new UntypedFormControl(!!this.tool.excludedFromReturn)
        });
        this.setFormValidators();

        this.getOwners();
        this.getLines();

        this.form.get('name')!.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            switchMap(value => {
                const trimmed = value?.trim();
            if (!trimmed) {
            const nameCtrl = this.form.get('name')!;
            const currentErrors = nameCtrl.errors || {};
            delete currentErrors['nameNotUnique'];
            nameCtrl.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
            return of(null);
            }
                return this.toolService.validateToolProperty(ToolProperty.Name, trimmed);
            })
            ).subscribe(result => {
            const nameCtrl = this.form.get('name')!;
            if (result === null) { return; }

            const currentErrors = { ...(nameCtrl.errors || {}) };
            delete currentErrors['nameNotUnique'];

            if (result.validationState !== ToolPropertyValidationState.Error) {
                nameCtrl.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
            } else {
                nameCtrl.setErrors({
                    ...currentErrors,
                    nameNotUnique: {
                        tools: (result.tools || []).map(tool => `${tool.toolUid} - ${tool.name}`).join(', ')
                    }
                });
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['tool'] || changes['owners']) && this.form) {
            this.loadFormValues(this.tool);
        }
    }

    public loadFormValues(tool: ToolFormModel) {
        this.form.setValue({
            isSet: false,
            setSize: 0,
            nrOfSpareTools: 0,
            name: tool.name,
            description: tool.description,
            owner: tool.owner,
            line: tool.line,
            isToolLifeMonitoredInSap: tool.isToolLifeMonitoredInSap,
            isToolLifeMonitoredInMes: tool.isToolLifeMonitoredInMes,
            isToolLifeMonitoredInPlc: tool.isToolLifeMonitoredInPlc,
            isToolStationedAtERSA: tool.isToolStationedAtERSA,
            excludedFromReturn: tool.excludedFromReturn,
            hasMaintenaceAtLine: tool.hasMaintenaceAtLine
        });
    }

    setFormValidators() {
        const isSetControl = this.form.get('isSet');
        const setSizeControl = this.form.get('setSize');
        const spareSizeControl = this.form.get('nrOfSpareTools');

        spareSizeControl.setValidators((c) => {
            return Validators.compose([Validators.min(0), Validators.max(this.tool.setSize)])(c);
        });

        setSizeControl.valueChanges.subscribe(() => {
            spareSizeControl.updateValueAndValidity();
        });

        isSetControl.valueChanges.subscribe((val) => {
            if (!val) {
                setSizeControl.disable();
                spareSizeControl.disable();
            } else {
                setSizeControl.enable();
                spareSizeControl.enable();
            }
        });
    }

    getFormValue(): ToolFormDetails {
        const formValue = this.form.value;
        if (!formValue.isSet) {
            formValue.setSize = 0;
            formValue.nrOfSpareTools = 0;
        }
        return formValue;
    }

    onSelectLine(line: Line) {
        this.selectedLine = line;
        this.changedLine.emit(line);
    }

    onSelectedOwner(owner: User) {
        this.selectedOwner = owner;
    }

    onSwitch(event) {
        this._logger.info('onSwitch:: Switch was changed', event);
        this.setSwitchToggled.emit(event);
    }

    resetForm() {
        this._logger.info('resetForm:: Resetting data with', this.tool);
        this.form.reset();
    }

    private changeSetSize(value) {
        this._logger.info('changeSetSize:: Changed set size', value);
        this.changedSetSize.emit(value);
    }

    get isFormValid() {
        return this.form.valid;
    }

    private getOwners() {
        this.userService.getUsers().subscribe((data) => {
            this.owners = data.users;
            this.loadFormValues(this.tool);
        });
    }

    private getLines() {
        this.lineService.getAllLines().subscribe((data) => {
            this.availableLines = data.items;
            this.loadFormValues(this.tool);
        });
    }
}
